import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, QrCode, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

// Generate a checkerboard QR-style visual (placeholder — real integration
// would embed the provider's actual QR payload).
function MockQR({ seed = "tjoef", colour = "var(--red)" }) {
  const size = 21; // 21x21 grid, feels retro
  const cells = [];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // deterministic pseudo-random cell fill
      const v = ((h ^ (x * 73856093)) ^ (y * 19349663)) & 7;
      const finder =
        (x < 7 && y < 7) ||
        (x >= size - 7 && y < 7) ||
        (x < 7 && y >= size - 7);
      const on = finder ? ((x + y) % 2 === 0) : v > 3;
      cells.push({ x, y, on });
    }
  }
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
      <rect x="0" y="0" width={size} height={size} fill="#fff" />
      {cells.filter((c) => c.on).map((c) => (
        <rect key={`${c.x}-${c.y}`} x={c.x} y={c.y} width="1" height="1" fill={colour} />
      ))}
      {[
        { x: 0, y: 0 },
        { x: size - 7, y: 0 },
        { x: 0, y: size - 7 },
      ].map(({ x, y }, i) => (
        <g key={i}>
          <rect x={x} y={y} width="7" height="7" fill="none" stroke="var(--text)" strokeWidth="1" />
          <rect x={x + 2} y={y + 2} width="3" height="3" fill="var(--text)" />
        </g>
      ))}
    </svg>
  );
}

export default function QRPayment() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data.order);
      if (data.order.status !== "pending_payment") {
        clearInterval(pollRef.current);
        navigate(`/orders/${orderId}`, { replace: true });
      }
    } catch (e) {
      /* transient */
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post("/orders/qr-confirm", { order_id: orderId });
      toast.success("Payment received!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
      setConfirming(false);
    }
  };

  if (!order) return <div className="text-[var(--text-2)]">Loading…</div>;
  const method = order.payment_method === "zapper" ? "Zapper" : "SnapScan";
  const brandColour = order.payment_method === "zapper" ? "var(--green)" : "var(--red)";

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="text-center">
        <span className="label-eyebrow">Scan to pay</span>
        <h1 className="mt-1 font-display text-5xl">Open {method}</h1>
        <p className="mt-2 text-sm text-[var(--text-2)]">
          Scan this code with the {method} app on your phone to complete the payment.
        </p>
      </div>

      <div className="card-panel p-6 flex flex-col items-center">
        <div
          className="w-64 h-64 border-2 border-[var(--text)] rounded-2xl p-3"
          style={{ background: "#fff", boxShadow: "5px 5px 0 0 var(--text)" }}
        >
          <MockQR seed={orderId} colour={brandColour} />
        </div>
        <p className="mt-6 font-display text-4xl">R{order.total.toFixed(2)}</p>
        <p className="mt-1 text-xs font-bold uppercase tracking-widest text-[var(--text-3)]">
          Order #{orderId.slice(-6).toUpperCase()} · Waiting for {method}…
        </p>
        <div className="mt-6 w-full">
          <div className="flex items-center gap-2 rounded-2xl border-2 border-[var(--text)] bg-[var(--yellow)] p-3">
            <Warning size={18} weight="fill" />
            <p className="text-[11px] font-bold uppercase tracking-widest">
              Demo mode — real {method} integration needs merchant credentials
            </p>
          </div>
          <button
            data-testid="qr-simulate-confirm"
            onClick={confirm}
            disabled={confirming}
            className="btn-primary w-full mt-3"
          >
            {confirming ? "Confirming…" : "Simulate payment (demo)"}
          </button>
          <button onClick={() => navigate("/cart")} className="btn-ghost w-full mt-3">
            Cancel and go back
          </button>
        </div>
      </div>

      <div className="card-panel p-5 flex items-center gap-3 text-sm">
        <QrCode size={22} weight="regular" />
        <div>
          <p className="font-bold">How this works in production</p>
          <p className="text-xs text-[var(--text-2)] mt-1">
            When your merchant account is live, {method} calls our webhook the moment a
            customer pays — this page will auto-advance to live tracking without a
            manual button press.
          </p>
        </div>
      </div>
    </div>
  );
}
